<script setup>
import {
  ref,
  watch,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    default: 'canned',
  },
  // Ancora a lista em `position: fixed`, medida do retângulo do campo, em vez
  // do `absolute ... bottom-full` do template. É OPT-IN: `position: absolute`
  // não escapa de ancestral com `overflow != visible`, e dentro do painel de
  // configuração do editor de fluxos (`.fe-inspector`, o único contêiner de
  // scroll da coluna) a lista saía cortada na borda. Onde o modo absoluto
  // funciona — caixa de resposta, respostas prontas, emoji, TemplateParser —
  // nada muda: sem a prop o elemento sai idêntico, sem atributo `style`.
  floating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['mentionSelect']);

const mentionsListContainerRef = ref(null);
const selectedIndex = ref(0);

// Mesmo teto do `max-h-[9.75rem]` que o modo absoluto usa via classe.
const MAX_HEIGHT = 156;
// Respiro para a lista não encostar na borda da viewport.
const VIEWPORT_MARGIN = 8;

// Classes comuns aos dois modos. As de posicionamento e tamanho ficam de fora
// porque no modo flutuante elas vêm do style inline medido do campo — deixar
// `bottom-full`/`w-full`/`max-h` na lista obrigaria a neutralizá-las com
// `auto` no style, que é mais frágil do que simplesmente não emiti-las.
const BASE_CLASS =
  'bg-surface-raised rounded-md overflow-auto z-20 pb-0 border border-solid border-line-faint mention--box';
const ANCHORED_POSITION_CLASS =
  'absolute w-full left-0 bottom-full max-h-[9.75rem]';

const containerClass = computed(() =>
  props.floating ? BASE_CLASS : `${BASE_CLASS} ${ANCHORED_POSITION_CLASS}`
);

const floatingStyle = ref(null);
// Resolvidos uma vez no mounted: com `position: fixed` o `offsetParent` do
// próprio elemento vira null, então a âncora precisa ser guardada antes.
let anchorEl = null;
let clipEl = null;

const isScrollable = node => {
  const { overflow, overflowX, overflowY } = window.getComputedStyle(node);
  return [overflow, overflowX, overflowY].some(
    value => value && value !== 'visible'
  );
};

// Sobe uma vez procurando as duas coisas:
// - âncora: o primeiro ancestral posicionado, que é o mesmo elemento contra o
//   qual o modo absoluto se posiciona (o offsetParent). A geometria de partida
//   fica idêntica nos dois modos; o `fixed` só muda quem recorta.
// - clip: o primeiro ancestral que recorta (o `.fe-inspector`, no editor de
//   fluxos). O `fixed` escapa dele — é por isso que a lista deixa de ser
//   cortada, mas também por isso ela continuaria visível depois de o campo
//   sair da área visível do painel.
const resolveContext = el => {
  let node = el?.parentElement || null;
  let anchor = null;
  let clip = null;
  while (node && node !== document.body) {
    if (!anchor && window.getComputedStyle(node).position !== 'static') {
      anchor = node;
    }
    if (!clip && isScrollable(node)) clip = node;
    if (anchor && clip) break;
    node = node.parentElement;
  }
  return { anchor: anchor || el?.parentElement || null, clip };
};

const updatePosition = () => {
  if (!props.floating) return;
  const el = mentionsListContainerRef.value;
  if (!el || !anchorEl) return;

  const rect = anchorEl.getBoundingClientRect();

  // Campo rolado para fora da área visível do painel: some junto, em vez de
  // ficar flutuando sobre o cabeçalho ou sobre o canvas.
  if (clipEl) {
    const clip = clipEl.getBoundingClientRect();
    if (rect.bottom <= clip.top || rect.top >= clip.bottom) {
      floatingStyle.value = { display: 'none' };
      return;
    }
  }

  const spaceAbove = Math.max(0, rect.top - VIEWPORT_MARGIN);
  const spaceBelow = Math.max(
    0,
    window.innerHeight - rect.bottom - VIEWPORT_MARGIN
  );
  // Mantém o lado de cima como preferência (é o que a lista sempre fez);
  // só vira para baixo quando lá em cima não cabe e embaixo sobra mais.
  const openUp = spaceAbove >= MAX_HEIGHT || spaceAbove >= spaceBelow;
  const maxHeight = Math.min(MAX_HEIGHT, openUp ? spaceAbove : spaceBelow);

  floatingStyle.value = {
    position: 'fixed',
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    maxHeight: `${maxHeight}px`,
    ...(openUp
      ? { bottom: `${window.innerHeight - rect.top}px` }
      : { top: `${rect.bottom}px` }),
  };
};

onMounted(() => {
  if (!props.floating) return;
  ({ anchor: anchorEl, clip: clipEl } = resolveContext(
    mentionsListContainerRef.value
  ));
  updatePosition();
  // `capture`: o scroll que move o campo é o do painel de configuração, e
  // evento de scroll de elemento não borbulha até a window.
  window.addEventListener('scroll', updatePosition, true);
  window.addEventListener('resize', updatePosition);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updatePosition, true);
  window.removeEventListener('resize', updatePosition);
});

const adjustScroll = () => {
  const container = mentionsListContainerRef.value;
  const item = container.querySelector(`#mention-item-${selectedIndex.value}`);
  if (item) {
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const containerTop = container.scrollTop;
    const containerBottom = containerTop + container.offsetHeight;
    if (itemTop < containerTop) {
      container.scrollTop = itemTop;
    } else if (itemBottom + 34 > containerBottom) {
      container.scrollTop = itemBottom - container.offsetHeight + 34;
    }
  }
};

const onSelect = () => {
  emit('mentionSelect', props.items[selectedIndex.value]);
};

useKeyboardNavigableList({
  items: computed(() => props.items),
  onSelect,
  adjustScroll,
  selectedIndex,
});

watch(
  () => props.items,
  newItems => {
    if (newItems.length < selectedIndex.value + 1) {
      selectedIndex.value = 0;
    }
    // A lista muda de tamanho conforme a busca, e o campo (textarea) cresce
    // junto: remede a âncora em vez de ficar preso na posição da abertura.
    if (props.floating) nextTick(updatePosition);
  }
);

watch(selectedIndex, adjustScroll);

const onHover = index => {
  selectedIndex.value = index;
};

const onListItemSelection = index => {
  selectedIndex.value = index;
  onSelect();
};

const variableKey = (item = {}) => {
  return props.type === 'variable' ? `{{${item.label}}}` : `/${item.label}`;
};
</script>

<template>
  <div
    ref="mentionsListContainerRef"
    :style="floatingStyle"
    :class="containerClass"
  >
    <ul class="mb-0 vertical dropdown menu">
      <woot-dropdown-item
        v-for="(item, index) in items"
        :id="`mention-item-${index}`"
        :key="item.key"
        class="!mb-0"
        @mouseover="onHover(index)"
      >
        <button
          class="flex group flex-col gap-0.5 overflow-hidden cursor-pointer items-start rounded-none py-2.5 px-2.5 justify-center w-full h-full text-left hover:bg-woot-50 dark:hover:bg-woot-800 border-x-0 border-t-0 border-b border-solid border-line-faint"
          :class="{
            ' bg-woot-25 dark:bg-woot-800': index === selectedIndex,
          }"
          @click="onListItemSelection(index)"
        >
          <slot :item="item" :index="index" :selected="index === selectedIndex">
            <p
              class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-body group-hover:text-woot-500 dark:group-hover:text-woot-500 text-ellipsis whitespace-nowrap"
              :class="{
                'text-woot-500 dark:text-woot-500': index === selectedIndex,
              }"
            >
              {{ item.description }}
            </p>
            <p
              class="max-w-full min-w-0 mb-0 overflow-hidden text-xs text-subtle group-hover:text-woot-500 dark:group-hover:text-woot-500 text-ellipsis whitespace-nowrap"
              :class="{
                'text-woot-500 dark:text-woot-500': index === selectedIndex,
              }"
            >
              {{ variableKey(item) }}
            </p>
          </slot>
        </button>
      </woot-dropdown-item>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.mention--box {
  .dropdown-menu__item:last-child > button {
    @apply border-0;
  }
}

.canned-item__button::v-deep .button__content {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}
</style>
